import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronDown, ShoppingCart, Download } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { exportToCsv } from '@/lib/exportCsv';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

const PAGE_SIZE = 20;
const GRADE_TONE: Record<string, 'neutral' | 'info' | 'warning' | 'primary'> = {
  Bronze: 'neutral',
  Silver: 'info',
  Gold: 'warning',
  Platinum: 'primary',
};
const PROVIDER_LABEL: Record<string, string> = {
  email: 'Email',
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};
const PROVIDER_ICON: Record<string, string> = {
  google: 'ri-google-fill',
  facebook: 'ri-facebook-fill',
  apple: 'ri-apple-fill',
};
const STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  placed: 'primary',
  confirmed: 'primary',
  shipped: 'warning',
  dispatched: 'warning',
  delivered: 'success',
  cod_collected: 'success',
  remitted: 'success',
  settled: 'success',
  cancelled: 'error',
  returned: 'error',
};

type Filter = 'all' | 'registered' | 'guest';

interface RegisteredRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  member_grade: string;
  created_at: string;
  signup_provider: string;
}

interface OrderRow {
  id: string;
  order_no: string;
  status: string;
  total_bdt: number;
  placed_at: string;
  customer_id: string | null;
  customer_info: { name?: string; phone?: string; address?: string } | null;
}

interface UnifiedRow {
  key: string;
  type: 'registered' | 'guest';
  id?: string;
  name: string;
  phone: string;
  email?: string | null;
  memberGrade?: string;
  orderCount: number;
  totalSpent: number;
  activityDate: string;
  lastOrderId: string | null;
  guestOrders?: OrderRow[];
  lastOrderProducts?: string;
  signupProvider?: string;
}

// One combined list — a customer is either a registered account
// (rxk_customers) or a guest checkout (core_orders.customer_id IS NULL,
// grouped by phone since that's the one stable thing a guest gives) — the
// "Registered"/"Guest" control filters this one list, it isn't two
// separate views. Order history (count/last order/products) is looked up
// for both kinds the same way, from the same core_orders fetch. (Same
// pattern as ERP's Customers page.)
export default function RxkCustomers() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState<RegisteredRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [lastOrderProducts, setLastOrderProducts] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      supabase
        .from('rxk_customers')
        .select('id, full_name, email, phone, member_grade, created_at, signup_provider')
        .order('created_at', { ascending: false })
        .limit(5000),
      supabase
        .from('core_orders')
        .select('id, order_no, status, total_bdt, placed_at, customer_id, customer_info')
        .eq('channel', 'rxk_web')
        .order('placed_at', { ascending: false })
        .limit(10000),
    ]).then(async ([custRes, orderRes]) => {
      if (!mounted) return;
      const orderRows = (orderRes.data as OrderRow[]) ?? [];
      setRegistered((custRes.data as RegisteredRow[]) ?? []);
      setOrders(orderRows);

      // One extra query for just the products in each customer's most
      // recent order, rather than pulling every line item for every order.
      const byCustomer = new Map<string, OrderRow[]>();
      for (const o of orderRows) {
        const key = o.customer_id ?? `phone:${o.customer_info?.phone?.trim() ?? o.id}`;
        const list = byCustomer.get(key) ?? [];
        list.push(o);
        byCustomer.set(key, list);
      }
      const lastOrderIds = [...byCustomer.values()].map((list) => list[0]?.id).filter((id): id is string => !!id);

      if (lastOrderIds.length > 0) {
        const { data: items } = await supabase
          .from('core_order_items')
          .select('order_id, sku, core_products(name_en)')
          .in('order_id', lastOrderIds);
        const productsByOrder = new Map<string, string[]>();
        for (const item of (items as { order_id: string; sku: string; core_products: { name_en: string } | null }[]) ?? []) {
          const name = item.core_products?.name_en ?? item.sku;
          const list = productsByOrder.get(item.order_id) ?? [];
          if (!list.includes(name)) list.push(name);
          productsByOrder.set(item.order_id, list);
        }
        const map: Record<string, string> = {};
        for (const [orderId, names] of productsByOrder) map[orderId] = names.join(', ');
        if (mounted) setLastOrderProducts(map);
      }

      if (mounted) setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const allRows = useMemo<UnifiedRow[]>(() => {
    const guestGroups = new Map<string, OrderRow[]>();
    for (const o of orders) {
      if (o.customer_id) continue;
      const phone = o.customer_info?.phone?.trim() || '';
      const key = phone || `no-phone:${o.id}`;
      const list = guestGroups.get(key) ?? [];
      list.push(o);
      guestGroups.set(key, list);
    }

    const registeredRows: UnifiedRow[] = registered.map((c) => {
      const own = orders.filter((o) => o.customer_id === c.id).sort((a, b) => (a.placed_at < b.placed_at ? 1 : -1));
      const lastOrder = own[0];
      return {
        key: `r:${c.id}`,
        type: 'registered',
        id: c.id,
        name: c.full_name,
        phone: c.phone ?? '—',
        email: c.email,
        memberGrade: c.member_grade,
        orderCount: own.length,
        totalSpent: own.reduce((sum, o) => sum + Number(o.total_bdt), 0),
        activityDate: lastOrder?.placed_at ?? c.created_at,
        lastOrderId: lastOrder?.id ?? null,
        lastOrderProducts: lastOrder ? lastOrderProducts[lastOrder.id] : undefined,
        signupProvider: c.signup_provider,
      };
    });

    const guestRows: UnifiedRow[] = [...guestGroups.entries()].map(([key, list]) => {
      const sorted = [...list].sort((a, b) => (a.placed_at < b.placed_at ? 1 : -1));
      const lastOrder = sorted[0];
      return {
        key: `g:${key}`,
        type: 'guest',
        name: lastOrder.customer_info?.name || 'Guest',
        phone: lastOrder.customer_info?.phone?.trim() || '—',
        orderCount: sorted.length,
        totalSpent: sorted.reduce((sum, o) => sum + Number(o.total_bdt), 0),
        activityDate: lastOrder.placed_at,
        lastOrderId: lastOrder.id,
        guestOrders: sorted,
        lastOrderProducts: lastOrderProducts[lastOrder.id],
      };
    });

    return [...registeredRows, ...guestRows].sort((a, b) => (a.activityDate < b.activityDate ? 1 : -1));
  }, [registered, orders, lastOrderProducts]);

  const rows = useMemo(() => {
    let list = filter === 'all' ? allRows : allRows.filter((r) => r.type === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) => r.name.toLowerCase().includes(q) || r.phone.toLowerCase().includes(q) || (r.email ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [allRows, filter, search]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleExport = () => {
    setExporting(true);
    try {
      const headers = ['SR', 'Name', 'Number', 'Email', 'Type', 'Last Order', 'Which Product', 'Order Count'];
      const csvRows = rows.map((r, i) => [
        i + 1,
        r.name,
        r.phone,
        r.email ?? '',
        r.type === 'registered' ? 'Registered' : 'Guest',
        r.activityDate ? r.activityDate.slice(0, 10) : '',
        r.lastOrderProducts ?? '',
        r.orderCount,
      ]);
      exportToCsv(`customers-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-[22px] font-bold text-ink">Customers (고객)</h1>
          <Button size="sm" variant="secondary" onClick={handleExport} disabled={exporting || rows.length === 0}>
            <Download className="w-3.5 h-3.5" /> Export ({rows.length})
          </Button>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1.5">
            {([
              { key: 'all' as const, label: 'All' },
              { key: 'registered' as const, label: 'Registered' },
              { key: 'guest' as const, label: 'Guest' },
            ]).map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  setFilter(f.key);
                  setPage(0);
                }}
                className={`text-[12.5px] font-semibold px-3.5 py-2 rounded-full cursor-pointer transition-colors ${
                  filter === f.key ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Field label="">
            <Input
              placeholder="Name, email, or phone…"
              value={search}
              onChange={(e) => {
                setPage(0);
                setSearch(e.target.value);
              }}
              className="!w-72"
            />
          </Field>
        </div>

        <Card>
          <CardHeader
            title="Customers"
            subtitle={rows.length > 0 ? `${page * PAGE_SIZE + 1}–${Math.min(rows.length, (page + 1) * PAGE_SIZE)} of ${rows.length}` : undefined}
            icon={<Users className="w-4 h-4" />}
            action={
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  Prev
                </Button>
                <span className="text-[11.5px] text-ink-faint px-1">{page + 1} / {totalPages}</span>
                <Button size="sm" variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
                  Next
                </Button>
              </div>
            }
          />
          <CardBody className="space-y-1.5">
            {loading ? (
              <Spinner />
            ) : pageRows.length === 0 ? (
              <EmptyState title="No customers found" />
            ) : (
              pageRows.map((r) => {
                if (r.type === 'registered') {
                  return (
                    <button
                      key={r.key}
                      onClick={() => navigate(`/admin/customers/${r.id}`)}
                      className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-semibold text-ink truncate">{r.name}</p>
                        <p className="text-[11px] text-ink-faint truncate">{r.email} · {r.phone}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge tone="info">Registered</Badge>
                        {r.signupProvider && r.signupProvider !== 'email' && (
                          <Badge tone="neutral">
                            <i className={`${PROVIDER_ICON[r.signupProvider] ?? ''} mr-1`}></i>
                            {PROVIDER_LABEL[r.signupProvider] ?? r.signupProvider}
                          </Badge>
                        )}
                        <Badge tone={GRADE_TONE[r.memberGrade ?? ''] ?? 'neutral'}>{r.memberGrade}</Badge>
                        <span className="text-[11px] text-ink-faint">{r.orderCount} order(s)</span>
                        <p className="text-[10.5px] text-ink-disabled w-24 text-right">{r.orderCount > 0 ? r.activityDate.slice(0, 10) : `Joined ${r.activityDate.slice(0, 10)}`}</p>
                      </div>
                    </button>
                  );
                }

                const isOpen = expanded.has(r.key);
                return (
                  <div key={r.key} className="rounded-lg border border-border-subtle overflow-hidden">
                    <button
                      onClick={() => toggleExpanded(r.key)}
                      className="w-full flex items-center justify-between px-2 py-2 hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-semibold text-ink truncate">{r.name}</p>
                        <p className="text-[11px] text-ink-faint truncate">{r.phone} · {r.orderCount} order(s)</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge tone="neutral">Guest</Badge>
                        <p className="text-[12.5px] font-bold text-ink tabular-nums">৳{r.totalSpent.toLocaleString()}</p>
                        <p className="text-[10.5px] text-ink-disabled w-24 text-right">{r.activityDate.slice(0, 10)}</p>
                        <ChevronDown className={`w-3.5 h-3.5 text-ink-faint transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-border-subtle bg-surface-subtle/40 px-3 py-2 space-y-1">
                        {r.guestOrders!.map((o) => (
                          <div key={o.id} className="flex items-center justify-between px-1 py-1.5">
                            <div className="flex items-center gap-1.5">
                              <ShoppingCart className="w-3 h-3 text-ink-faint" />
                              <span className="text-[12px] font-semibold text-ink">{o.order_no}</span>
                              <span className="text-[10.5px] text-ink-faint">{o.placed_at.slice(0, 10)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{o.status}</Badge>
                              <span className="text-[12px] font-bold text-ink tabular-nums">৳{Number(o.total_bdt).toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
